import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { match } from 'path-to-regexp';

import { Loading } from '@autopass-web/mochi-component';

import { PATHS } from '../configs';
import { useInvoice } from '../services/use-invoice';

const AUTH_PATHS = ['auth/taiwan-pay'];

const Invoices = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const location = useLocation();
  const isInvoiceIdValid = typeof invoiceId === 'string';

  const { invoice, error, isLoading } = useInvoice(
    { invoiceId },
    {
      enabled: isInvoiceIdValid,
    },
  );

  const expectPathname = (() => {
    if (isLoading) {
      return;
    }

    if (!isInvoiceIdValid) {
      return PATHS.NOTFOUND;
    }

    if (error) {
      return PATHS.INVOICES(invoiceId).ERROR[''];
    }

    if (invoice.expires_in < 1 || invoice.state === 'expired') {
      return PATHS.INVOICES(invoiceId).ERROR.EXPIRED;
    }

    if (invoice.state === 'rejected') {
      return PATHS.INVOICES(invoiceId).ERROR.USER_CANCELLED;
    }

    if (invoice.state === 'canceled' || invoice.state === 'canceling') {
      return PATHS.INVOICES(invoiceId).ERROR.STORE_CANCELLED;
    }

    if (
      invoice.payment_state === 'paid' ||
      invoice.payment_state === 'authorized'
    ) {
      // 若目前在 auth 頁面（如 taiwan-pay），先讓該頁面完成 confirm API 再導向
      const isOnAuthPage = AUTH_PATHS.some((path) =>
        location.pathname.endsWith(path),
      );

      if (isOnAuthPage) {
        return location.pathname;
      }

      if (invoice?.callback_url) {
        return invoice.callback_url;
      }

      return PATHS.INVOICES(invoiceId).SUCCESS;
    }

    if (
      invoice.payment_state === 'unauthorized' ||
      invoice.payment_state === 'unpaid'
    ) {
      // pass to invoices page,
      return PATHS.INVOICES(invoiceId)[''];
    }

    // if not specific pattern, just pass the router redirector
    if (match(PATHS.INVOICES(invoiceId)[''] + '*any')(location.pathname)) {
      return location.pathname;
    }

    return PATHS.INVOICES(invoiceId)[''];
  })();

  useEffect(() => {
    if (!expectPathname) {
      return;
    }

    const isExternal =
      expectPathname.startsWith('http://') ||
      expectPathname.startsWith('https://');

    if (isExternal) {
      if (expectPathname !== window.location.href) {
        window.location.href = expectPathname;
      }
    } else {
      if (expectPathname !== location.pathname) {
        navigate(expectPathname);
      }
    }
  }, [expectPathname, location.pathname, navigate]);

  if (isLoading) {
    return <Loading />;
  }

  if (expectPathname === location.pathname) {
    return <Outlet />;
  }

  return null;
};

export { Invoices };
